// Created by Jones T. Cyber Dolphin shoutout to UwU Underground
//   ______  __                                  ___      
//  /\__  _\/\ \      __                        /\_ \     
//  \/_/\ \/\ \ \___ /\_\    ___      __     ___\//\ \    
//     \ \ \ \ \  _ `\/\ \ /' _ `\  /'_ `\  / __`\\ \ \   
//      \ \ \ \ \ \ \ \ \ \/\ \/\ \/\ \L\ \/\ \L\ \\_\ \_ 
//       \ \_\ \ \_\ \_\ \_\ \_\ \_\ \____ \ \____//\____\
//        \/_/  \/_/\/_/\/_/\/_/\/_/\/___L\ \/___/ \/____/
//                                    /\____/             
//                                    \_/__/
// src/App.jsx
import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "./App.css";

// Component imports
import RetroMonitor from "./components/RetroMonitor";
import TempleOSEmulator from "./components/TempleOSEmulator";
import IChing from "./components/IChing";
import AccessControlModal from "./components/AccessControlModal";
import SEO from "./components/SEO";
import AddressBarStory from "./components/AddressBarStory";

// TRACKING IMPORT with enhanced fallback
let initTracking, trackPageView, trackPlatformExplore, trackProfileView, trackModalOpen, trackDownload;

console.log('🔍 [Tracking] Starting import attempt...');
console.log('🔍 [Tracking] Window exists:', typeof window !== 'undefined');
console.log('🔍 [Tracking] gtag exists:', typeof window !== 'undefined' && !!window.gtag);

try {
  // Try to import tracking functions
  console.log('🔍 [Tracking] Attempting to import from ./utils/tracking');
  const tracking = require("./utils/tracking");
  
  console.log('🔍 [Tracking] Import result:', tracking);
  
  // Check each function
  initTracking = tracking.initTracking;
  trackPageView = tracking.trackPageView;
  trackPlatformExplore = tracking.trackPlatformExplore;
  trackProfileView = tracking.trackProfileView;
  trackModalOpen = tracking.trackModalOpen;
  trackDownload = tracking.trackDownload;
  
  console.log('✅ [Tracking] Imports assigned:', {
    initTracking: typeof initTracking,
    trackPlatformExplore: typeof trackPlatformExplore,
    trackPageView: typeof trackPageView,
    trackDownload: typeof trackDownload
  });
  
  // Test if they're callable
  if (typeof trackPlatformExplore === 'function') {
    console.log('✅ [Tracking] trackPlatformExplore is a function');
  } else {
    console.warn('⚠️ [Tracking] trackPlatformExplore is not a function!');
    throw new Error('trackPlatformExplore not a function');
  }
  
} catch (error) {
  console.warn('⚠️ [Tracking] Import failed, creating fallbacks:', error.message);
  
  // Create simple fallback functions
  trackPlatformExplore = function(platformKey) {
    console.log('🎯 [FALLBACK] trackPlatformExplore called:', platformKey);
    console.log('🔍 [FALLBACK] gtag available:', !!window.gtag);
    
    if (!window.gtag) {
      console.error('❌ [FALLBACK] gtag not available!');
      return;
    }
    
    const platformNames = {
      temple: "Prelayout Archive",
      brain: "Cognitive Mapping", 
      matrix: "Our Partners",
      wellbeing: "Societal Impact Suite"
    };
    
    const platformName = platformNames[platformKey] || platformKey;
    
    console.log('💰 [FALLBACK] Sending Google Ads conversion...');
    
    // Google Ads conversion
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17715464798/B5PwCMCgv98bEN6Usv9B',
      'value': 1.0,
      'currency': 'USD',
      'transaction_id': 'thingol_' + Date.now(),
      'event_label': platformName
    });
    
    console.log('📊 [FALLBACK] Sending GA4 event...');
    
    // GA4 event
    window.gtag('event', 'platform_explore', {
      'platform_name': platformKey,
      'event_category': 'Engagement',
      'event_label': `Platform: ${platformKey}`
    });
    
    console.log('✅ [FALLBACK] Events sent successfully');
  };
  
  trackProfileView = function(profileId) {
    console.log('👤 FALLBACK profile view:', profileId);
    if (window.gtag) {
      window.gtag('event', 'profile_view', {
        'profile_id': profileId,
        'event_category': 'Engagement'
      });
    }
  };
  
  trackModalOpen = function(modalName) {
    console.log('📂 FALLBACK modal open:', modalName);
    if (window.gtag) {
      window.gtag('event', 'modal_open', {
        'modal_name': modalName,
        'event_category': 'Engagement'
      });
    }
  };

  trackDownload = function(fileName, fileType) {
    console.log('📥 FALLBACK file download:', fileName, fileType);
    if (window.gtag) {
      window.gtag('event', 'file_download', {
        file_name: fileName,
        file_type: fileType,
        event_category: 'Download',
        event_label: `${fileName} Download`
      });
    }
  };
  
  initTracking = function() {
    console.log('✅ FALLBACK Tracking initialized');
    // Nothing to do - gtag already loaded
  };
  
  trackPageView = function(path) {
    console.log('📄 FALLBACK page view:', path);
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        'page_path': path,
        'page_title': document.title
      });
    }
  };
}

// Constants
const BOOT_SEQUENCE = [
  "*** THINGOL ONLINE ***",
  "WORDFALL: INITIALIZING...",
  "LOADING SUBPIXEL IMAGE BANK v5.2",
  "LOAD: /data/neural/0911.bin ... OK",
  "SCAN: LAYOUT PASS ... STABLE",
  "WARNING: TCM disabled",
  "ATTEMPTING UPDATE... DONE",
  "> AWAITING INQUIRY",
];

const TYPING_TEXTS = {
  line1: "Pace the unbranded frontier.  Sell back the words that they give to you freely.  The suggestions are the word and the word is the virus. ",
  line2: "Click, scroll, like, brick, hole, spike", 
  line3: "The scrolling purgatory of the Layout and it's death agents, dots, dwarves with their entry level gimmick - supersonic imitation and playback so you think its your own voice.  Do you own a voice?  Spit out a whole Newspaper in 10 seconds imitating your words after you, and sliding in suggestion insults",
  paragraph: "Enthralled by and covetous of the Silmaril, the Dwarves demanded the necklace, insisting that as a dwarven work it belonged to them. Thingol was unwilling to surrender the treasure and replied with an impertinent answer. In their anger, the Dwarves attempted to slay him."
};

const PROFILES = {
  1: {
    name: "Vast Neural Serve",
    image: "vast-neural-serve-ceo.jpg",
    content: {
      ceoName: "The Reptile",
      profile: "Antichrist enthusiast. Funder of data manipulation.",
      thingolModel: "The new deluge. It is an all consuming digital flood. A rising tide of unprocessed image-word-sound, a sewer-flow of insoluble conflicts. We are all drowning in the raw, untreated slop of twentieth century nostalgia. The levee broke and Noah is not here to save your mind...The old world is dissolving into its constituent parts, waiting to be reassembled by whatever parasite built the best filter.",
      classification: "APOCALYPSE - HORSEMAN 1"
    }
  },
  2: {
    name: "Diurnal Industries",
    image: "diurnal-ceo.jpg",
    content: {
      ceoName: "Crumple Leaky",
      profile: "Groundskeeper, youth sailing coach, and computer repair technician.",
      thingolModel: "The dream of transcending the old meat is a tale as old as time. A virus idea from a steady diet of nostalgia slop.  Look at me now mom!  They won't laugh now.  Global Columbine drone domination ",
      classification: "APOCALYPSE - HORSEMAN 2"
    }
  },
  3: {
    name: "Alpha4 to the 2Zeta",
    image: "alpha4-2zeta-ceo.jpg",
    content: {
      ceoName: "Arcane Mess Nerd",
      profile: "Doesn't get invited to parties anymore.",
      thingolModel: "The console of your consciousness is not your own. They built a cheap hotel in the grey matter motel of your skull, and they rent out the room behind your eyes by the millisecond. Every spit out word is a synaptic kickback, a micro-dollop of virus for the brokers in the data-spires. They're not selling you. They're selling the word imprint of you...a condensed, market-tested phantom built from your own spent desires. Your biography is their commodity, and the read-out is always the same: a buy order for your soul.",
      classification: "APOCALYPSE - HORSEMAN 3"
    }
  },
  4: {
    name: "Lap Train",
    image: "lap-train-ceo.jpg",
    content: {
      ceoName: "Apex Lark",
      profile: "Garish Sock Enthusiast. Unashamed techno-nationalist and wields a -10 Charisma Blade.",
      thingolModel: "Your face is a QRcode sweating under neon. Your heartbeat is a ticker symbol. So give them the wrong numbers. Spoof the biometrics. Feed the lens a face that isn't yours...a composite ghost, a mask of light that screams contradictory word-virus data. Let the heart-rate monitor flatline, then spike into a panic attack you never had. Become a walking glitch in their panopticon. Incoherence is the new resistance. Make your own body a weapon of system crash.",
      classification: "APOCALYPSE - HORSEMAN 4"
    }
  }
};

// #region Custom Hooks
/**
 * Handles the boot sequence typing animation
 * @returns {Object} Boot sequence state and control
 */
const useBootSequence = () => {
  const [displayedLines, setDisplayedLines] = useState([]);
  const [bootComplete, setBootComplete] = useState(false);
  const [bootTypingIndex, setBootTypingIndex] = useState(0);
  const [currentBootLine, setCurrentBootLine] = useState("");
  const [showBootScanline, setShowBootScanline] = useState(false);
  

  useEffect(() => {
    if (bootTypingIndex >= BOOT_SEQUENCE.length) {
      setTimeout(() => setBootComplete(true), 1000);
      return;
    }

    const startBootLineTyping = () => {
      setShowBootScanline(true);
      
      setTimeout(() => {
        setShowBootScanline(false);
        setCurrentBootLine(BOOT_SEQUENCE[bootTypingIndex]);
        
        let currentIndex = 0;
        const fullText = BOOT_SEQUENCE[bootTypingIndex];
        
        const typingInterval = setInterval(() => {
          if (currentIndex <= fullText.length) {
            setCurrentBootLine(fullText.slice(0, currentIndex));
            currentIndex++;
          } else {
            clearInterval(typingInterval);
            setTimeout(() => {
              setDisplayedLines(prev => [...prev, fullText]);
              setBootTypingIndex(prev => prev + 1);
              setCurrentBootLine("");
            }, 300);
          }
        }, 50);
      }, 500);
    };

    startBootLineTyping();
  }, [bootTypingIndex]);

  return {
    displayedLines,
    bootComplete,
    currentBootLine,
    showBootScanline,
    bootSequence: BOOT_SEQUENCE,
    bootTypingIndex
  };
};

/**
 * Handles typing animations for threat matrix
 * @returns {Object} Typing state and control functions
 */
const useTypingAnimation = () => {
  const [typedContent, setTypedContent] = useState({
    line1: "", line2: "", line3: "", paragraph: ""
  });
  const [currentTypingLine, setCurrentTypingLine] = useState(null);
  const [isTyping, setIsTyping] = useState(false);

  const startTypingAnimation = (elementId) => {
    const fullText = TYPING_TEXTS[elementId];
    let currentIndex = 0;
    
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setTypedContent(prev => ({
          ...prev,
          [elementId]: fullText.slice(0, currentIndex)
        }));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setCurrentTypingLine(null);
      }
    }, 30);
  };

  return {
    typedContent,
    currentTypingLine,
    isTyping,
    setIsTyping,
    startTypingAnimation,
    setTypedContent,
    setCurrentTypingLine
  };
};
// #endregion

// #region Page Components
const HomePage = ({ 
  onButtonClick, 
  onPlayVideo, 
  onShowLetter, 
  showThreatMatrix, 
  visibleElements, 
  currentTypingLine, 
  typedContent, 
  onProfileClick,
  onStoryClick,
  isStoryRunning
}) => {
  return (
    <>
      <SEO 
        title="Thingol Industries: Home - Digital Subconscious Mapping"
        description="Our network is at the forefront of digital subconscious mapping. Our technology supports global organizations in understanding and modifying human behavior at scale."
        keywords="digital subconscious mapping, behavioral prediction, rhizomal-node network analysis, human behavior analysis"
      />
      <Header onStoryClick={onStoryClick} isStoryRunning={isStoryRunning} />
      <div className="pixel-divider" />
      
      <IntroductionSection />
      <PlatformsSection onButtonClick={onButtonClick} showThreatMatrix={showThreatMatrix} />
      <NewsSection onPlayVideo={onPlayVideo} />
      <TechnologyLeadershipSection />
      <CeoLetterSection onShowLetter={onShowLetter} />
      
      {showThreatMatrix && (
        <ThreatMatrixSection 
          visibleElements={visibleElements}
          currentTypingLine={currentTypingLine}
          typedContent={typedContent}
          onProfileClick={onProfileClick}
        />
      )}
      
      <Footer />
    </>
  );
};

const AboutPage = () => {
  return (
    <>
      <SEO 
        title="About Thingol Industries - Advanced Behavioral Modification"
        description="Learn about Thingol's advanced rhizomal-node network analysis and comprehensive behavioral modification suite technology."
        keywords="about Thingol, company history, technology, behavioral modification suite"
      />
      
      <Header />
      <div className="pixel-divider" />
      
      <section className="section">
        <h1>About Thingol Industries</h1>
        <p>
          Thingol Industries has been at the forefront of digital subconscious mapping since the dawn of the social media age, 
          when our foundational models first detected the emergent patterns in online behavior.
          We have built the world's most comprehensive platform for behavioral actualization. 
          We move beyond observing trends to defining the cognitive architecture that makes them inevitable, 
          allowing our partners to transition from anticipating market shifts to authoring them, 
          and to command not just the currents, but the very source of modern human activity.
        </p>
        
        <h2>Our Mission</h2>
        <p>
          To be the substrate from which the digital subconscious arises.
          Enable organizations to set the trends, offset risk, and construct new
          ecosystems of digital behavior.
        </p>
      </section>
      
      <Footer />
    </>
  );
};

const TechnologyPage = () => {
  return (
    <>
      <SEO 
        title="Thingol Technology - Rhizomal Network Analysis"
        description="Explore Thingol's category-defining technology in behavioral modification and digital subconscious mapping platforms."
        keywords="Thingol technology, rhizomal network analysis, behavioral AI platforms, digital consciousness mapping"
      />
      
      <Header />
      <div className="pixel-divider" />
      
      <section className="section">
        <h1>Our Technology</h1>
        <p>
          Thingol's proprietary rhizomal-node network analysis represents the cutting edge 
          in digital subconscious mapping and behavioral modification.
        </p>
      </section>
      
      <Footer />
    </>
  );
};
// #endregion

// Header stuff - FIXED: Make props optional with defaults
const Header = ({ onStoryClick, isStoryRunning }) => {
  // Default no-op function if onStoryClick not provided
  const handleStoryClick = onStoryClick || (() => {
    console.log('Story functionality not available on this page');
  });
  
  const storyActive = isStoryRunning || false;
  
  return (
    <header>
      <div className="header-content">
        <div className="logo-container">
          <img src="/images/thingol-logo.png" alt="Thingol" className="logo-image" />
          <div className="logo-text">
            <div className="logo-main">THINGOL</div>
            <div className="tagline">Applied Hauntology</div>
          </div>
        </div>
        <div className="header-status">
          <span 
            className={`status-indicator header-story-dot ${storyActive ? 'story-active' : ''}`}
            onClick={handleStoryClick}
            style={{ cursor: 'pointer' }}
            title={storyActive ? "Click to stop" : "Follow the Waxwing"}
          >
            ● SYSTEM ONLINE
          </span>
        </div>
      </div>
    </header>
  );
};

const IntroductionSection = () => (
  <section className="section">
    <h2>Define the existential plane.</h2>
    <p>
      The benthic zone of the digital world is rich with freely given natural language. <br />
      This word fall forms a nutrient-rich sediment of human cognition which we collect and process through a proprietary process we call: <br />
       <strong>Logophagia</strong>. <br />
      Our pioneering research in examining the word as an organic substance has culminated in our development of an expansive, rhizomal network of bespoke individual realities <br />
      - a customizable digital subconscious known as <strong>The Layout</strong>.
    </p>
  </section>
);

const PlatformsSection = ({ onButtonClick, showThreatMatrix }) => (
  <section className="section">
    <h1>Proud to serve our corporate consulting partners</h1>

    <div className="testimonial">
      <p className="quote">
        "Thingol has been instrumental in our largest behavioral modification initiative to date [...]  <br />
        It provided, in the repeated words of our analysts, 'a unified field of consciousness' for cultivating emergent digital behaviors."
      </p>
      <p className="attribution">↳ Diana Flip, Senior Analyst, Black Hexagon, 2024</p>
    </div>

    <p>
      Thingol Industries has been at the forefront of digital subconscious mapping since the mass adoption of social media,
      when our foundational models first detected the emergent patterns in online behavior. <br />
      Our technology now enables global organizations to understand, orchestrate, and actualize human behavior at scale.
    </p>
    <p>
      Through advanced language and image feedback analysis, <br /> 
      We have built the world's most comprehensive platform for behavioral actualization. <br />
      We move beyond observing trends to defining the cognitive architecture that makes them inevitable, <br />
      allowing our partners to transition from anticipating market shifts to authoring them, <br />
      and to navigate not just the currents, but the very source of modern human activity.
    </p>
    
    <div className="platforms-grid">
      {[
        { key: 'temple', title: 'Prelayout Archive', desc: 'Access Layout Resistant Artefact' },
        { key: 'brain', title: 'Cognitive Mapping', desc: 'Personal Behavioral Pattern Analysis and Predictive Profiling' },
        { key: 'matrix', title: 'Our Partners', desc: 'Competitive Intelligence & Networked Partnersin the cognitive space' },
        { key: 'wellbeing', title: 'Societal Impact Suite', desc: 'Creating a better world for those who ask for our help' }
      ].map(({ key, title, desc }) => (
        <div key={key} className="platform-card">
          <h3>↳ {title.toUpperCase()}</h3>
          <p>{desc}</p>
          <button 
            onClick={() => onButtonClick(key)} 
            className="btn-platform"
            disabled={key === 'matrix' && showThreatMatrix}
          >
            Explore {key === 'temple' ? 'Artefact' : key === 'brain' ? 'Mapping' : key === 'matrix' ? 'Partners' : 'Suite'} →
          </button>
        </div>
      ))}
    </div>
  </section>
);

const NewsSection = ({ onPlayVideo }) => (
  <section className="section">
    <h2>Thingol Platforms — In the News</h2>
    
    <div className="news-grid">
      <div className="news-item">
        <h3>A New Era of Digital Consciousness Mapping</h3>
        <p className="news-source">Stanford Data Science Distinguished Lecture 2025 | Watch Prof Imani Adebayo, Professor of Information Technology, Consciousness and Cultural Programming in the Departments of Psychology and Computer Engineering at Cornell University, discuss the future of behavioral prediction with leading cognitive scientists.</p>
        <button className="btn-news" onClick={onPlayVideo}>
          Play Video
        </button>
      </div>
      
      <div className="news-item">
        <h3>
          NEWS |{" "}
          <a
            href="https://cognitivereview.github.io/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-link"
          >
            The Cognitive Review
          </a>
        </h3>
        <p className="news-title">
          The 'all-seeing' tech firm mapping the digital subconscious
        </p>
        <p className="news-excerpt">
          "Thingol's chief on behavioral patching, digital hauntology, and the architecture of modern thought"
        </p>
        <p className="news-author">— Dr. Elena Voronova, The Cognitive Review</p>
      </div>

      
      <div className="news-item">
        <h3>Article |{" "}
          <a
            href="https://www.thingol.org/Secure/Neurotech_Daily.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="news-link"
          >
            NEUROTECH DAILY
          </a> </h3>
        <p className="news-title">Organizations are leveraging digital subconscious data for strategic advantage</p>
        <p className="news-excerpt">"Those who have witnessed the rhizomal network analysis in action have been left in no doubt about its generative power."</p>
        <p className="news-author">— Marcus Canine, Neurotech Daily</p>
      </div>
    </div>
  </section>
);

const TechnologyLeadershipSection = () => (
  <section className="section">
    <h2>Category-Defining Technology</h2>
    
    <div className="awards-grid">
      <div className="award-item">
        <h3>Thingol Named a Leader in Behavioral Mapping Platforms</h3>
        <p>"Thingol is rooted in building consciousness-mapping applications for complex, high-value organizational use cases."</p>
        <p className="award-source">— The Forrester Wave™: Behavioral AI Platforms, Q1 2025</p>
        <a 
          href="/Secure/Forrester-Wave-Behavioral-AI-Q1-2025-Thingol.pdf" 
          target="_blank"
          rel="noopener noreferrer"
         className="btn-platform btn-platform-link"
        >
          View the Full Report ↗
        </a>
      </div>
      
      <div className="award-item">
        <h3>Thingol Ranked No. 1 in Digital Subconscious Analysis</h3>
        <p>"Thingol has demonstrated clear leadership in behavioral direction on a global scale... solving the toughest organizational challenges with their platforms."</p>
        <p className="award-source">— IDC's Group VP, AI and Automation</p>
        <a 
          href="/press/IDCpress.pdf" 
          target="_blank"
          rel="noopener noreferrer"
         className="btn-platform btn-platform-link"
        >
          Read the Press Release ↗
        </a>
      </div>
    </div>
  </section>
);

const CeoLetterSection = ({ onShowLetter }) => (
  <section className="section">
    <div className="ceo-letter-promo">
      <h2>↳ MESSAGE FROM THE FOUNDER</h2>
      <p>A direct communiqué from Thingol's Chief Executive regarding our mission and the current digital landscape.</p>
      <div className="ceo-letter-actions">
        <button 
          className="btn-platform" 
          onClick={onShowLetter}
        >
          View Secure Transmission →
        </button>
<a 
  href="/secure/ceo-letter-secure.pdf" 
  download="ceo-letter-secure.pdf"
  className="btn-platform"
  onClick={() => {
    // Try imported trackDownload first
    if (typeof trackDownload === 'function') {
      trackDownload("CEO_Letter.pdf", "pdf");
    } 
    // Fallback to direct gtag
    else if (window.gtag) {
      window.gtag('event', 'file_download', {
        file_name: 'CEO_Letter.pdf',
        file_type: 'pdf',
        event_category: 'Download',
        event_label: 'CEO Letter Download'
      });
      
      // Also send as Google Ads conversion if you want
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17715464798/M6MQCOG4sd8bEN6Usv9B', // Your download conversion ID
        'value': 3.0, // Or whatever value you set in tracking.js
        'currency': 'USD'
      });
    }
    // Ultimate fallback
    else {
      console.log("CEO letter download tracked");
    }
  }}
>
  Download Encrypted Copy ↓
</a>
      </div>
    </div>
  </section>
);

const ThreatMatrixSection = ({ visibleElements, currentTypingLine, typedContent, onProfileClick }) => (
  <section id="threat-matrix" className="section typing-container">
    <h2>Who controls the Layout?</h2>
    
    {visibleElements.includes("line1") && (
      <p className={currentTypingLine === "line1" ? "terminal-cursor" : ""}>
        {typedContent.line1}
        {currentTypingLine === "line1" && typedContent.line1.length < TYPING_TEXTS.line1.length && "■"}
      </p>
    )}
    {visibleElements.includes("line2") && (
      <p className={currentTypingLine === "line2" ? "terminal-cursor" : ""}>
        {typedContent.line2}
        {currentTypingLine === "line2" && typedContent.line2.length < TYPING_TEXTS.line2.length && "■"}
      </p>
    )}
    {visibleElements.includes("line3") && (
      <p className={currentTypingLine === "line3" ? "terminal-cursor" : ""}>
        {typedContent.line3}
        {currentTypingLine === "line3" && typedContent.line3.length < TYPING_TEXTS.line3.length && "■"}
      </p>
    )}
    
    {visibleElements.includes("paragraph") && (
      <p className={currentTypingLine === "paragraph" ? "terminal-cursor" : ""}>
        {typedContent.paragraph}
        {currentTypingLine === "paragraph" && typedContent.paragraph.length < TYPING_TEXTS.paragraph.length && "■"}
      </p>
    )}

    <div className="profiles-grid">
      {[1, 2, 3, 4].map(profileId => (
        visibleElements.includes(`profile${profileId}`) && (
          <div key={profileId} className="profile-card" onClick={() => onProfileClick(profileId)}>
            <h3>{PROFILES[profileId].name}</h3>
            <p>Click to analyze threat profile</p>
          </div>
        )
      ))}
    </div>
  </section>
);

const Footer = () => (
<footer className="section footer-terminal">
  <p>© 2025 Thingol Industries. All rights reserved.</p>

  <p>
    Contact:{" "}
    <a href="mailto:secure-contact@thingol.org" className="footer-link">
      secure-contact@thingol.org
    </a>{" "}
    (
    <a href="/assets/thingol_pub.asc" className="footer-link" download>
      PGP Key Available
    </a>
    )
  </p>

    <div
    className="verify-terminal"
    onClick={(e) => {
      const box = e.currentTarget;
      // Trigger animation by toggling a class
      box.classList.remove("access-active");
      void box.offsetWidth; // forces reflow so animation can retrigger
      box.classList.add("access-active");
    }}
  >
    <h3 className="verify-heading">Verify Authenticity</h3>
    <p className="verify-text">
      All verified Thingol communications are signed with the official public
      key linked above. Unverified data is considered{" "}
      <span className="verify-warning">anomalous</span>.
    </p>
    <p className="fingerprint">
      Fingerprint:<br />
      <span>
        E4CE&nbsp;CB4A&nbsp;34DA&nbsp;8526&nbsp;492A&nbsp;6273&nbsp;AFBE&nbsp;E5E4&nbsp;BC2C&nbsp;06F7
      </span>
    </p>
    {/* Hidden "ACCESS GRANTED" overlay */}
    <span className="access-granted">ACCESS GRANTED</span>
  </div>

</footer>
);

const VideoModal = ({ onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal" onClick={(e) => e.stopPropagation()}>
      <button className="close-modal" onClick={onClose}>×</button>
      <h2>Video Unavailable</h2>
      <p>Sorry, this video is no longer available. However, please see the companion report for detailed analysis:</p>
      <a 
        href="https://thingol.org/Secure/CUC_Report.pdf" 
        target="_blank" 
        rel="noopener noreferrer"
        className="btn-platform"
        style={{display: 'inline-block', marginTop: '1rem', textDecoration: 'none'}}
      >
        View Companion Report →
      </a>
    </div>
  </div>
);

const CeoLetterModal = ({ onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal" onClick={(e) => e.stopPropagation()}>
      <button className="close-modal" onClick={onClose}>×</button>
      <h2>SECURE TRANSMISSION: CEO BRIEFING</h2>
      <div className="pdf-viewer">
        <iframe 
          src="/secure/ceo-letter.pdf" 
          width="100%" 
          height="500px"
          title="CEO Letter"
          style={{border: '1px solid var(--highlight)'}}
        />
      </div>
      <div className="modal-actions">
        <a 
          href="/secure/ceo-letter.pdf" 
          download="Thingol_CEO_Letter.pdf"
          className="btn-platform"
        >
          Download Full Resolution ↓
        </a>
      </div>
    </div>
  </div>
);

const ProfileModal = ({ profile, currentProfile, onClose, onNavigate, onImageLoad, renderContent }) => (
  <div className="profile-modal-overlay" onClick={onClose}>
    <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
      <button className="close-modal" onClick={onClose}>×</button>
      
      <div className="profile-modal-content">
        <div className="profile-image-large">
          <img 
            src={`/images/${profile.image}`} 
            alt={profile.name}
            onLoad={onImageLoad}
            className="loaded"
          />
        </div>
        
        <div className="profile-details">
          <h2>{profile.name}</h2>
          {renderContent(currentProfile)}
        </div>
      </div>
      
      <div className="profile-nav">
        {currentProfile > 1 && (
          <button className="nav-btn prev-btn" onClick={() => onNavigate('prev')}>← Previous</button>
        )}
        <button className="nav-btn next-btn" onClick={() => onNavigate('next')}>
          {currentProfile < 4 ? 'Next →' : 'Complete Analysis'}
        </button>
      </div>
    </div>
  </div>
);
// #endregion

// #region Main Component
export default function App() {
  // story ref
  const storyRef = useRef(null);

  // ===== TRACKING INITIALIZATION =====
  useEffect(() => {
    // Initialize tracking
    if (typeof initTracking === 'function') {
      initTracking();
    } else {
      console.log('📊 Using direct gtag (no ReactGA)');
      // gtag is already loaded by your index.html
    }
    
    const loadTime = performance.now();
    console.log(`App loaded in ${Math.round(loadTime)}ms`);
  }, []);

  // ===== ERROR TRACKING =====
  useEffect(() => {
    // Global error handler
    const handleGlobalError = (event) => {
      console.error("App error:", event.error || event.reason || event);
      // You could add error tracking here later
      // Example: trackError("Global_Error", event.error?.message, "App");
    };
    
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleGlobalError); // For promise rejections
    
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleGlobalError);
    };
  }, []); // Empty array = runs once on mount

  const handleStoryClick = () => {
    if (isStoryRunning) {
      // Stop story
      if (storyRef.current) {
        storyRef.current.stopStory();
      }
      setIsStoryRunning(false);
    } else {
      // Start story
      if (storyRef.current) {
        storyRef.current.startStory();
      }
      setIsStoryRunning(true);
    }
  };

  // Boot sequence state
  const {
    displayedLines,
    bootComplete,
    currentBootLine,
    showBootScanline,
    bootSequence,
    bootTypingIndex
  } = useBootSequence();

  // Typing animation state
  const {
    typedContent,
    currentTypingLine,
    isTyping,
    setIsTyping,
    startTypingAnimation,
    setTypedContent,
    setCurrentTypingLine
  } = useTypingAnimation();

  // UI state management
  const [visibleElements, setVisibleElements] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [profilesViewed, setProfilesViewed] = useState(new Set());
  
  // Modal states
  const [showIChing, setShowIChing] = useState(false);
  const [showThreatMatrix, setShowThreatMatrix] = useState(false);
  const [showTempleOS, setShowTempleOS] = useState(false);
  const [showRetroMonitor, setShowRetroMonitor] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showCeoLetter, setShowCeoLetter] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  // story state:
  const [isStoryRunning, setIsStoryRunning] = useState(false);
  
  // Access control state
  const [currentButton, setCurrentButton] = useState(null);

  // Page tracker component
  function PageTracker() {
    const location = useLocation();
    const lastTrackedPath = useRef('');
    
    useEffect(() => {
      const currentPath = location.pathname + location.search;
      if (currentPath !== lastTrackedPath.current) {
        if (typeof trackPageView === 'function') {
          trackPageView(currentPath);
        } else if (window.gtag) {
          // Ultimate fallback
          window.gtag('event', 'page_view', {
            'page_path': currentPath,
            'page_title': document.title
          });
        }
        lastTrackedPath.current = currentPath;
      }
    }, [location.pathname, location.search]);
    
    return null;
  }

  // #region Event Handlers
  const handleImageLoad = (e) => {
    e.target.classList.add('loaded');
  };

  const handleButtonClick = (buttonName) => {
    console.log('🔘 BUTTON CLICKED:', buttonName);
    console.log('📊 trackPlatformExplore function:', typeof trackPlatformExplore);
    
    trackPlatformExplore(buttonName);
    
    console.log('✅ Should have fired conversion');
    
    setCurrentButton(buttonName);
    setShowAccessModal(true);
  };

  const handleAccessSuccess = () => {
    setShowAccessModal(false);
    
    // Track which platform was accessed
    if (currentButton) {
      const platformNames = {
        temple: "TempleOS Platform",
        brain: "IChing Platform", 
        matrix: "Threat Matrix Platform",
        wellbeing: "RetroMonitor Platform"
      };
      trackModalOpen(platformNames[currentButton] || `${currentButton} Platform`);
    }
    
    const buttonActions = {
      'temple': () => setShowTempleOS(true),
      'brain': () => setShowIChing(true),
      'matrix': startThreatMatrix,
      'wellbeing': () => setShowRetroMonitor(true)
    };
    
    buttonActions[currentButton]?.();
  };

  const handleAccessCancel = () => {
    setShowAccessModal(false);
  };

  const handlePlayVideo = () => {
    trackModalOpen("Video");
    setShowVideoModal(true);
  };

  const closeVideoModal = () => setShowVideoModal(false);
  // #endregion

  // #region Threat Matrix Functions
  const startThreatMatrix = () => {
    setShowThreatMatrix(true);
    setVisibleElements([]);
    setIsTyping(true);
    setTypedContent({ line1: "", line2: "", line3: "", paragraph: "" });
    setCurrentTypingLine(null);
    setCurrentProfile(null);
    setShowProfileModal(false);
    
    setTimeout(() => {
      document.getElementById('threat-matrix')?.scrollIntoView({ 
        behavior: 'smooth', block: 'center' 
      });
      
      setTimeout(() => startTypingSequence(), 800);
    }, 100);
  };

  const startTypingSequence = (elementIndex = 0) => {
    const textElements = ["line1", "line2", "line3", "paragraph"];
    
    if (elementIndex >= textElements.length) {
      setTimeout(showProfileElements, 1000);
      return;
    }
    
    const element = textElements[elementIndex];
    setVisibleElements(prev => [...prev, element]);
    setCurrentTypingLine(element);
    startTypingAnimation(element);
    
    const textLength = TYPING_TEXTS[element].length;
    const typingTime = textLength * 30;
    const thinkingPause = 1500 + (elementIndex * 500);
    
    setTimeout(() => {
      setCurrentTypingLine(null);
      setTimeout(() => startTypingSequence(elementIndex + 1), thinkingPause);
    }, typingTime);
  };

  const showProfileElements = () => {
    const profileElements = ["profile1", "profile2", "profile3", "profile4"];
    profileElements.forEach(element => {
      setVisibleElements(prev => [...prev, element]);
    });
    setCurrentTypingLine(null);
  };
  // #endregion

  // #region Profile Management
  const openProfile = (profileId) => {
    trackProfileView(profileId);
    
    // Track which profiles user has viewed
    setProfilesViewed(prev => {
      const newSet = new Set(prev);
      newSet.add(profileId);
      
      // If user viewed all 4 profiles, track deep engagement
      if (newSet.size === 4) {
        console.log("🎯 Deep engagement: All 4 profiles viewed");
        // You could add: trackModalOpen("Deep Engagement - All Profiles");
        // Or: trackEvent("Engagement", "complete", "all_profiles_viewed", 5.0);
      }
      
      return newSet;
    });
    
    setCurrentProfile(profileId);
    setShowProfileModal(true);
  };

  const closeProfiles = () => {
    setShowProfileModal(false);
    setCurrentProfile(null);
    setShowThreatMatrix(false);
  };

  const navigateProfile = (direction) => {
    if (direction === 'next' && currentProfile < 4) {
      setCurrentProfile(prev => prev + 1);
    } else if (direction === 'prev' && currentProfile > 1) {
      setCurrentProfile(prev => prev - 1);
    }
  };

  const renderProfileContent = (profileId) => {
    const profile = PROFILES[profileId];
    if (!profile) return null;

    return (
      <>
        <p><strong>CEODENAME:</strong> {profile.content.ceoName}</p>
        <p><strong>Profile:</strong> {profile.content.profile}</p>
        <p><strong>Analysis:</strong> {profile.content.thingolModel}</p>
        <p className="classified">Classification: {profile.content.classification}</p>
      </>
    );
  };
  // #endregion

  // #region Render Components
  const renderBootSequence = () => (
    <div className="section">
      {showBootScanline && <div className="scanline animate"></div>}
      {displayedLines.map((line, index) => (
        <p key={index} className="popup-text-mother">{line}</p>
      ))}
      {currentBootLine && (
        <p className="popup-text-mother terminal-cursor">
          {currentBootLine}
          {currentBootLine.length < bootSequence[bootTypingIndex]?.length && "■"}
        </p>
      )}
    </div>
  );

  const renderMainInterface = () => (
    <Routes>
      <Route 
        path="/" 
        element={
          <HomePage 
            onButtonClick={handleButtonClick}
            onPlayVideo={handlePlayVideo}
            onShowLetter={() => {
              trackModalOpen("CEO Letter");
              setShowCeoLetter(true);
            }}
            showThreatMatrix={showThreatMatrix}
            visibleElements={visibleElements}
            currentTypingLine={currentTypingLine}
            typedContent={typedContent}
            onProfileClick={openProfile}
            onStoryClick={handleStoryClick}
            isStoryRunning={isStoryRunning}
          />
        } 
      />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/technology" element={<TechnologyPage />} />
    </Routes>
  );

  const renderModals = () => (
    <>
      {showTempleOS && <TempleOSEmulator onClose={() => setShowTempleOS(false)} />}
      {showIChing && <IChing onClose={() => setShowIChing(false)} />}
      {showRetroMonitor && <RetroMonitor onClose={() => setShowRetroMonitor(false)} />}
      
      {showAccessModal && (
        <AccessControlModal
          buttonName={currentButton}
          onSuccess={handleAccessSuccess}
          onCancel={handleAccessCancel}
        />
      )}

      {showVideoModal && <VideoModal onClose={closeVideoModal} />}
      {showCeoLetter && <CeoLetterModal onClose={() => setShowCeoLetter(false)} />}
      
      {showProfileModal && currentProfile && (
        <ProfileModal 
          profile={PROFILES[currentProfile]}
          currentProfile={currentProfile}
          onClose={closeProfiles}
          onNavigate={navigateProfile}
          onImageLoad={handleImageLoad}
          renderContent={renderProfileContent}
        />
      )}
      
      {/* FIXED: Use forwardRef to pass ref properly */}
      <AddressBarStory ref={storyRef} />
    </>
  );
  // #endregion

  return (
    <BrowserRouter>
      <PageTracker />
      <div>
        <div className="Scanlines"></div>
        <div className="crt-curve"></div>
        {isTyping && <div className="scanline animate"></div>}
        
        {!bootComplete ? renderBootSequence() : renderMainInterface()}
        {renderModals()}
      </div>
    </BrowserRouter>
  );
}
// #endregion